import ListItem from 'bloko/common/listItem';

/**
 * Многоцелевая иерархическая коллекция,
 * свободная от специфического поведения компонентов.
 * @constructor
 */
function TreeCollection() {
    /** @type {Array.<ListItem>} */
    const models = [];
    /** @type {Array.<ListItem>} */
    const topLevelModels = [];

    const modelsById = {};
    const parentsById = {};
    const childrenById = {};

    /**
     * Создаёт из объекта модель и добавляет в коллекцию.
     * @param {Object} attrs
     * @param {String|undefined} [parentId]
     */
    function addModel(attrs, parentId) {
        const model = ListItem.from(attrs);
        if (typeof getModel(model.id) !== 'undefined') {
            console.error(`ID "${model.id}" already exists`);
            return;
        }
        models.push(model);
        modelsById[model.id] = model;
        if (parentId) {
            parentsById[model.id] = parentId;
            childrenById[parentId] = childrenById[parentId] || [];
            childrenById[parentId].push(model.id);
        } else {
            topLevelModels.push(model);
        }
    }

    /**
     * Возвращает весь массив моделей.
     * @returns {Array.<ListItem>}
     */
    function toList() {
        return models.slice();
    }

    /**
     * Возвращает массив моделей первого уровня (без родителя).
     * @returns {Array.<ListItem>}
     */
    function getTopLevel() {
        return topLevelModels.slice();
    }

    /**
     * Возвращает модель по ID или `undefined`.
     * @param {String} id
     * @returns {ListItem|undefined}
     */
    function getModel(id) {
        return modelsById.hasOwnProperty(id) ? modelsById[id] : undefined;
    }

    /**
     * Возвращает ID родителя по ID модели или `undefined`.
     * @param {String} id
     * @returns {String|undefined}
     */
    function getParentId(id) {
        return parentsById.hasOwnProperty(id) ? parentsById[id] : undefined;
    }

    /**
     * Возвращает модель родителя по ID или `undefined`.
     * @param {String} id
     * @returns {ListItem|undefined}
     */
    function getParent(id) {
        return getModel(getParentId(id));
    }

    /**
     * Возвращает массив ID родителей от ближнего к дальнему.
     * @param {String} id ID текущей модели.
     * @returns {Array.<String>}
     */
    function getParentIds(id) {
        const parentIds = [];
        let parentId = getParentId(id);
        while (parentId) {
            parentIds.push(parentId);
            parentId = getParentId(parentId);
        }
        return parentIds;
    }

    /**
     * Возвращает массив моделей родителей от ближнего к дальнему.
     * @param {String} id ID текущей модели.
     * @returns {Array.<ListItem>}
     */
    function getParents(id) {
        return getParentIds(id).map(getModel);
    }

    /**
     * Возвращает наличие дочерних моделей по ID родителя.
     * @param {String} id
     * @returns {Boolean}
     */
    function hasChildren(id) {
        return childrenById.hasOwnProperty(id);
    }

    /**
     * Возвращает список ID дочерних по ID родителя.
     * @param {String} id
     * @returns {Array.<String>}
     */
    function getChildrenIds(id) {
        return hasChildren(id) ? childrenById[id].slice() : [];
    }

    /**
     * Возвращает список дочерних моделей по ID родителя.
     * @param {String} id ID текущей модели.
     * @returns {Array.<ListItem>}
     */
    function getChildren(id) {
        return getChildrenIds(id).map(getModel);
    }

    /**
     * Рекурсивно проходит по списку моделей, применяет к каждой модели переданную функцию.
     * @param {Array.<ListItem>} items Список моделей для обработки.
     * @param {WalkCallback} callback Вызываемая функция.
     * @param {Array.<ListItem>} [parents] Массив моделей родителей от ближнего к дальнему.
     *     Используется в случаях, когда на вход поступают модели из середины дерева, имеющие своих родителей.
     * @private
     */
    function _walk(items, callback, parents) {
        const currentParents = parents ? parents.slice() : [];
        items.forEach((item) => {
            callback(item, currentParents);
            const children = getChildren(item.id);
            if (children && children.length) {
                _walk(children, callback, [item].concat(currentParents));
            }
        });
    }

    /**
     * Рекурсивно проходит по дереву, применяет к каждой модели переданную функцию.
     * @param {WalkCallback} callback Вызываемая функция.
     */
    function walk(callback) {
        _walk(getTopLevel(), callback);
    }

    /**
     * Рекурсивно проходит по дочерним элементам указанной модели, применяет к каждому переданную функцию.
     * @param {String} id ID модели, с которой начинать обход.
     * @param {WalkCallback} callback Вызываемая функция.
     */
    function walkChildren(id, callback) {
        const children = getChildren(id);
        if (typeof children !== 'undefined') {
            const parents = [getModel(id)].concat(getParents(id));
            _walk(children, callback, parents);
        }
    }

    /**
     * Проходит по родителям модели до самого верха, применяет к каждому указанную функцию.
     * @param {String} id ID текущей модели.
     * @param {WalkCallback} callback Вызываемая функция.
     */
    function walkParents(id, callback) {
        const parents = getParents(id);
        while (parents.length) {
            callback(parents.shift(), parents.slice());
        }
    }

    /**
     * Возвращает коллекцию в виде дерева.
     * @param {WalkCallback} [filter] Функция-фильтр.
     *     Если указана, оставляет в дереве только те элементы, для которых вернулось `true`.
     * @returns {Array.<TreeItem>}
     */
    function toTree(filter) {
        const filteredTree = [];
        const modelsById = {};
        walk((model, parents) => {
            if (!filter || filter(model, parents)) {
                const treeItem = ListItem.from(model);
                modelsById[model.id] = treeItem;
                if (parents.length === 0) {
                    filteredTree.push(treeItem);
                } else {
                    const parent = modelsById[parents[0].id];
                    if (!parent.items) {
                        parent.items = [];
                    }
                    parent.items.push(treeItem);
                }
            }
        });
        return filteredTree;
    }

    /**
     * Возвращает новую коллекцию с отфильтрованными элементами и их родителями.
     * @param {WalkCallback} [filterFunc] Функция-фильтр.
     * @returns {TreeCollection}
     */
    function filter(filterFunc) {
        const filteredCollection = new TreeCollection();
        const ids = new Set();
        walk((model, parents) => {
            if (filterFunc(model, parents)) {
                ids.add(model.id);
                parents.forEach((parent) => {
                    ids.add(parent.id);
                });
            }
        });
        ids.forEach((id) => {
            filteredCollection.addModel(getModel(id), getParentId(id));
        });
        return filteredCollection;
    }

    this.addModel = addModel;
    this.toList = toList;
    this.getTopLevel = getTopLevel;
    this.getModel = getModel;
    this.getParentId = getParentId;
    this.getParent = getParent;
    this.getParentIds = getParentIds;
    this.getParents = getParents;
    this.hasChildren = hasChildren;
    this.getChildrenIds = getChildrenIds;
    this.getChildren = getChildren;
    this.walk = walk;
    this.walkChildren = walkChildren;
    this.walkParents = walkParents;
    this.toTree = toTree;
    this.filter = filter;
}

export default TreeCollection;
